'use client';

import { Fragment, useEffect, useRef, useState } from 'react';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { resendVerificationEmail } from '@eliadmin/core/src/api/email-verify/email-verify-api';
import Link from '@eliadmin/core/src/components/link';
import {
  FormResultMessage,
  type FormResult,
} from '@eliadmin/core/src/components/nt/account/shell/form-result';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { fillTemplate } from '@eliadmin/core/src/utils/nt/text';

import type { RegisteredAccount, RegisterTexts } from './types';
import { formatCountdown, useCooldown } from '../shared/use-cooldown';

/** Seconds before the activation link can be sent again (the password reset waits as long). */
const RESEND_COOLDOWN = 60;

/** „Na adresu {email} sme poslali …“ with the address in Body/L Bold (Figma). */
function DoneText({ text, email }: { text: string; email: string }) {
  const parts = text.split('{email}');

  return (
    <p className="nt-body-l text-[#363D36]">
      {parts.map((part, index) => (
        <Fragment key={index}>
          {index > 0 ? <strong className="nt-body-l-bold break-all">{email}</strong> : null}
          {part}
        </Fragment>
      ))}
    </p>
  );
}

/**
 * Figma „Účet bol úspešne vytvorený“ (221:14567, mobile 290:34108) in place of the form: #F5F6F3
 * card, radius 16, padding 40 (20 / 16 on the phone), 24 px gaps (16), the 44 px green check, the
 * title (Heading/H2 as the page's h1) over the activation text – only when the store sent an
 * activation link (`verified === false`) – then the actions (the designed welcome points are not
 * shown: the store has no setting for a registration bonus). The customer is signed in after the registration, so the primary button is
 * „Môj profil“ (or the page the registration came from) instead of the designed „Prihlásiť sa“;
 * „Poslať aktivačný odkaz znova“ resends with the core call and waits 60 s, the countdown below.
 */
export function RegisterDone({
  account,
  texts,
  returnUrl,
}: {
  account: RegisteredAccount;
  texts: RegisterTexts;
  returnUrl?: string;
}) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<FormResult>(null);
  const { remaining, start } = useCooldown();
  const unverified = account.verified === false;

  // The form is gone: start reading at the new state.
  useEffect(() => {
    window.scrollTo({ top: 0 });
    titleRef.current?.focus({ preventScroll: true });
  }, []);

  const resend = async () => {
    setResult(null);
    setSending(true);
    try {
      await resendVerificationEmail({ customerId: account.customerId, locale });
      start(RESEND_COOLDOWN);
    } catch (error) {
      const message =
        error instanceof ValidationError ? Object.values(error.fields).flat().join('\n') : '';
      setResult({ status: 'failed', message: message || t('text530') });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="flex w-full flex-col items-center gap-4 rounded-2xl bg-[#F5F6F3] px-4 py-5 text-center sm:gap-6 sm:p-10">
      <Icon name="check-circle" size={44} className="text-[#127A26]" />
      <div className="flex w-full max-w-[608px] flex-col gap-3 sm:gap-4">
        <h1 ref={titleRef} tabIndex={-1} className="nt-h2 text-[#0D110D] outline-none">
          {texts.doneTitle}
        </h1>
        {unverified && texts.doneText ? (
          <DoneText text={texts.doneText} email={account.email} />
        ) : null}
      </div>
      <div className="flex w-full flex-col justify-center gap-3 sm:w-auto sm:flex-row">
        <Button asChild iconLeft="user">
          <Link href={returnUrl ?? ROUTES.PROFILE() ?? '/'} prefetch={false}>
            {t('text1134')}
          </Link>
        </Button>
        {unverified && texts.resend ? (
          <Button
            type="button"
            variant="outline"
            iconLeft="arrow-clockwise"
            isLoading={sending}
            disabled={remaining > 0}
            onClick={resend}
          >
            {texts.resend}
          </Button>
        ) : null}
      </div>
      {unverified ? (
        <p role="status" className="nt-body-m text-[#5D655D] empty:-mt-4 sm:empty:-mt-6">
          {remaining > 0 && texts.countdown
            ? fillTemplate(texts.countdown, { cas: formatCountdown(remaining) })
            : ''}
        </p>
      ) : null}
      {/* Live regions stay mounted while empty (announced on change); no gap of their own then. */}
      <FormResultMessage result={result} className="empty:-mt-4 sm:empty:-mt-6" />
    </section>
  );
}
